/**
 * Design System Tokens — Nexus Admin
 *
 * These tokens define the visual language of the admin interface.
 * All colors, typography, spacing, and shapes should reference these constants.
 *
 * Based on: src/apps/admin/frontend/DESIGN.md
 */

export const designTokens = {
  colors: {
    // Surface palette (tonal system)
    surface: '#f8f9ff',
    'surface-dim': '#cbdbf5',
    'surface-bright': '#f8f9ff',
    'surface-container-lowest': '#ffffff',
    'surface-container-low': '#eff4ff',
    'surface-container': '#e5eeff',
    'surface-container-high': '#dce9ff',
    'surface-container-highest': '#d3e4fe',

    // On-surface
    'on-surface': '#0b1c30',
    'on-surface-variant': '#434656',

    // Inverse
    'inverse-surface': '#213145',
    'inverse-on-surface': '#eaf1ff',

    // Outline
    outline: '#737688',
    'outline-variant': '#c3c5d9',
    'surface-tint': '#004ced',

    // Primary
    primary: '#003ec7',
    'on-primary': '#ffffff',
    'primary-container': '#0052ff',
    'on-primary-container': '#dfe3ff',
    'inverse-primary': '#b7c4ff',

    // Secondary
    secondary: '#855300',
    'on-secondary': '#ffffff',
    'secondary-container': '#fea619',
    'on-secondary-container': '#684000',

    // Tertiary
    tertiary: '#a10016',
    'on-tertiary': '#ffffff',
    'tertiary-container': '#c5242b',
    'on-tertiary-container': '#ffddda',

    // Error
    error: '#ba1a1a',
    'on-error': '#ffffff',
    'error-container': '#ffdad6',
    'on-error-container': '#93000a',

    // Fixed colors
    'primary-fixed': '#dde1ff',
    'primary-fixed-dim': '#b7c4ff',
    'on-primary-fixed': '#001452',
    'on-primary-fixed-variant': '#0038b6',

    'secondary-fixed': '#ffddb8',
    'secondary-fixed-dim': '#ffb95f',
    'on-secondary-fixed': '#2a1700',
    'on-secondary-fixed-variant': '#653e00',

    'tertiary-fixed': '#ffdad7',
    'tertiary-fixed-dim': '#ffb3ad',
    'on-tertiary-fixed': '#410004',
    'on-tertiary-fixed-variant': '#930013',

    // Background
    background: '#f8f9ff',
    'on-background': '#0b1c30',

    // Surface variant
    'surface-variant': '#d3e4fe',

    // Functional accents (from DESIGN.md)
    warning: '#F59E0B', // Amber - non-destructive alerts
    destructive: '#EF4444', // Red - irreversible actions
    success: '#10B981', // Emerald - active statuses

    // Legacy/neutral scale
    slate: {
      50: '#F8FAFC',
      100: '#F1F5F9',
      200: '#E2E8F0',
      300: '#CBD5E1',
      400: '#94A3B8',
      500: '#64748B',
      600: '#475569',
      700: '#334155',
      800: '#1E293B',
      900: '#0F172A',
    },
  },

  typography: {
    'headline-xl': {
      fontFamily: 'Inter',
      fontSize: '32px',
      fontWeight: '700',
      lineHeight: '40px',
      letterSpacing: '-0.02em',
    },
    'headline-lg': {
      fontFamily: 'Inter',
      fontSize: '24px',
      fontWeight: '600',
      lineHeight: '32px',
      letterSpacing: '-0.01em',
    },
    'headline-md': {
      fontFamily: 'Inter',
      fontSize: '20px',
      fontWeight: '600',
      lineHeight: '28px',
    },
    'body-lg': {
      fontFamily: 'Inter',
      fontSize: '16px',
      fontWeight: '400',
      lineHeight: '24px',
    },
    'body-md': {
      fontFamily: 'Inter',
      fontSize: '14px',
      fontWeight: '400',
      lineHeight: '20px',
    },
    'label-md': {
      fontFamily: 'Inter',
      fontSize: '13px',
      fontWeight: '600',
      lineHeight: '18px',
      letterSpacing: '0.05em',
    },
    'label-sm': {
      fontFamily: 'Inter',
      fontSize: '12px',
      fontWeight: '500',
      lineHeight: '16px',
    },
    code: {
      fontFamily: 'Courier Prime',
      fontSize: '13px',
      fontWeight: '400',
      lineHeight: '20px',
    },
  },

  rounded: {
    sm: '0.25rem', // 4px
    DEFAULT: '0.5rem', // 8px
    md: '0.75rem', // 12px
    lg: '1rem', // 16px
    xl: '1.5rem', // 24px
    full: '9999px',
  },

  spacing: {
    base: 4,
    xs: 4,
    sm: 8,
    md: 16,
    lg: 24,
    xl: 32,
    gutter: 24,
    'margin-mobile': 16,
    'sidebar-width': 260,
  },

  elevation: {
    level0: {
      background: '#F8FAFC',
      shadow: 'none',
    },
    level1: {
      background: '#FFFFFF',
      border: '1px solid #E2E8F0',
      shadow: '0px 1px 3px rgba(0,0,0,0.05), 0px 10px 15px -5px rgba(0,0,0,0.03)',
    },
    level2: {
      background: '#FFFFFF',
      border: '1px solid #E2E8F0',
      shadow: '0px 4px 6px -1px rgba(0,0,0,0.1), 0px 10px 20px -5px rgba(0,0,0,0.06)',
    },
  },

  // Component-specific tokens
  components: {
    button: {
      primary: {
        background: '#003ec7',
        color: '#ffffff',
      },
      secondary: {
        background: '#ffffff',
        border: '1px solid #E2E8F0',
        color: '#0F172A',
      },
      ghost: {
        background: 'transparent',
        color: '#003ec7',
      },
      destructive: {
        background: '#EF4444',
        color: '#ffffff',
      },
    },
    sidebar: {
      width: 260,
      activeBar: '#003ec7',
      activeBackground: '#EFF6FF',
    },
    card: {
      paddingDesktop: 24,
      paddingMobile: 16,
      borderRadius: 12,
      border: '1px solid #E2E8F0',
    },
    table: {
      headerBackground: '#F8FAFC',
      rowHover: '#F8FAFC',
      border: '1px solid #E2E8F0',
      cellPadding: '12px',
    },
    input: {
      border: '1px solid #CBD5E1',
      focusBorder: '#003ec7',
      focusShadow: '0 0 0 3px rgba(0, 82, 255, 0.15)',
      errorBorder: '#EF4444',
    },
    statusBadge: {
      active: {
        background: '#D1FAE5',
        color: '#065F46',
      },
      suspended: {
        background: '#FEF3C7',
        color: '#92400E',
      },
      revoked: {
        background: '#FEE2E2',
        color: '#991B1B',
      },
    },
  },
} as const;

export type DesignTokens = typeof designTokens;
